<template>
  <div class="toast-container" aria-live="polite" aria-relevant="additions">
    <TransitionGroup name="toast-slide">
      <div
        v-for="toast in toasts"
        :key="toast.id"
        class="toast"
        :class="toast.type"
        role="alert"
        @click="$emit('remove', toast.id)"
      >
        <i :class="toast.icon" aria-hidden="true" />
        <div class="toast-content">
          <strong>{{ toast.title }}</strong>
          <p v-if="toast.message">{{ toast.message }}</p>
        </div>
        <button
          type="button"
          class="toast-close"
          aria-label="Dismiss notification"
          @click.stop="$emit('remove', toast.id)"
        >
          <i class="fa-solid fa-times" aria-hidden="true" />
        </button>
        <div
          v-if="toast.progress !== undefined"
          class="toast-progress"
          :style="{ width: toast.progress + '%' }"
        />
      </div>
    </TransitionGroup>
  </div>
</template>

<script>
export default {
  name: "ToastContainer",
  props: {
    toasts: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["remove"],
};
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 80px;
  right: max(12px, env(safe-area-inset-right));
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: min(400px, calc(100vw - 24px));
  pointer-events: none;
}

.toast-container .toast {
  pointer-events: auto;
}

.toast {
  position: relative;
  min-width: min(320px, 100%);
  max-width: 100%;
  background: var(--background-color2);
  color: var(--text-color1);
  border: 1px solid var(--border-color-hover1);
  border-left: 4px solid var(--border-color-hover);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 0;
  box-shadow: 0 8px 24px var(--box-shadow);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.toast:hover {
  transform: translateX(-4px);
  box-shadow: 0 10px 28px var(--box-shadow);
}

.toast i:first-child {
  font-size: 1.25rem;
  margin-top: 2px;
  flex-shrink: 0;
  color: var(--border-color-hover);
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-content strong {
  display: block;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 4px;
  color: var(--text-color1);
  line-height: 1.35;
}

.toast-content p {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: var(--text-color1);
  opacity: 0.88;
  word-break: break-word;
}

.toast-close {
  background: transparent;
  border: none;
  color: var(--text-color1);
  cursor: pointer;
  padding: 4px;
  font-size: 0.875rem;
  flex-shrink: 0;
  opacity: 0.65;
  transition: opacity 0.2s ease;
  line-height: 1;
}

.toast-close:hover {
  opacity: 1;
}

.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: var(--border-color-hover);
  transition: width 0.05s linear;
}

.toast-slide-enter-active,
.toast-slide-leave-active {
  transition: all 0.3s ease;
}

.toast-slide-enter-from,
.toast-slide-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

@media (max-width: 640px) {
  .toast-container {
    top: 60px;
    right: 10px;
    left: 10px;
    max-width: none;
  }

  .toast {
    min-width: 0;
    padding: 12px;
  }
}
</style>
